import React from 'react';
import './Tooltip.scss';

interface TooltipProps {
    id?: string;
    content: React.ReactNode;
    children: React.ReactElement;
    className?: string;
}

const AUTO_HIDE_MS = 3000;
function Tooltip({
    id,
    content,
    children,
    className,
}: TooltipProps): React.ReactElement {
    const generatedId = React.useId();
    const tooltipId = id || generatedId;
    const [show, setShow] = React.useState(false);
    const anchorRef = React.useRef<HTMLSpanElement>(null);
    const hideTimer = React.useRef<number>();

    const clearAutoHide = React.useCallback(() => {
        if (hideTimer.current) window.clearTimeout(hideTimer.current);
    }, []);

    const open = React.useCallback((): void => {
        clearAutoHide();
        setShow(true);
    }, [clearAutoHide]);

    const close = React.useCallback((): void => {
        clearAutoHide();
        setShow(false);
    }, [clearAutoHide]);
    const onPointerUp = React.useCallback(
        (event: React.PointerEvent<HTMLSpanElement>): void => {
            if (event.pointerType !== 'touch') return;
            open();
            hideTimer.current = window.setTimeout(close, AUTO_HIDE_MS);
        },
        [open, close]
    );

    React.useEffect(() => {
        if (!show) return undefined;
        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') close();
        };
        const onPointerDown = (event: PointerEvent): void => {
            if (
                anchorRef.current &&
                !anchorRef.current.contains(event.target as Node)
            ) {
                close();
            }
        };
        const onScroll = (): void => close();
        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('pointerdown', onPointerDown);
        window.addEventListener('scroll', onScroll, true);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('pointerdown', onPointerDown);
            window.removeEventListener('scroll', onScroll, true);
        };
    }, [show, close]);

    React.useEffect(() => clearAutoHide, [clearAutoHide]);

    const trigger = React.cloneElement(children, {
        'aria-describedby': tooltipId,
    });

    return (
        <span
            className={['rd-tt-anchor', className].filter(Boolean).join(' ')}
            ref={anchorRef}
            onMouseEnter={open}
            onMouseLeave={close}
            onFocus={open}
            onBlur={close}
            onPointerUp={onPointerUp}
        >
            {trigger}
            <span
                className="rd-tt"
                role="tooltip"
                id={tooltipId}
                data-show={show || undefined}
                aria-hidden={!show}
            >
                {content}
            </span>
        </span>
    );
}

Tooltip.defaultProps = {
    id: undefined,
    className: undefined,
};

export default Tooltip;
